/**
 * Snap-to-Wall geometry utilities.
 * Finds the nearest point on any polygon edge to a given cursor position.
 */

const SNAP_THRESHOLD = 15; // SVG coordinate units
const VERTEX_SNAP_THRESHOLD = 12;

/**
 * Find the closest point on line segment AB to point P.
 * Returns { x, y, dist }
 */
function closestPointOnSegment(px, py, ax, ay, bx, by) {
  const dx = bx - ax;
  const dy = by - ay;
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) return { x: ax, y: ay, dist: Math.hypot(px - ax, py - ay) };

  let t = ((px - ax) * dx + (py - ay) * dy) / lenSq;
  t = Math.max(0, Math.min(1, t));

  const cx = ax + t * dx;
  const cy = ay + t * dy;
  return { x: Math.round(cx), y: Math.round(cy), dist: Math.hypot(px - cx, py - cy) };
}

/**
 * Find the snap target for a cursor position against all other polygon edges.
 *
 * @param {number} cursorX - Current cursor X in SVG coords
 * @param {number} cursorY - Current cursor Y in SVG coords
 * @param {Array} polygons - All polygons array
 * @param {string|null} excludePolygonId - Polygon being edited (exclude from snapping)
 * @param {number} threshold - Snap distance threshold
 * @returns {{ x: number, y: number, type: 'edge'|'vertex', edgeFrom?: [number,number], edgeTo?: [number,number] } | null}
 */
export function findSnapTarget(cursorX, cursorY, polygons, excludePolygonId = null, threshold = SNAP_THRESHOLD) {
  let best = null;
  let bestDist = threshold;

  for (const poly of polygons) {
    if (poly.id === excludePolygonId) continue;
    const verts = poly.polygon;
    if (!verts || verts.length < 2) continue;

    // Check vertices first (higher priority — snap to corners)
    for (const v of verts) {
      const d = Math.hypot(cursorX - v[0], cursorY - v[1]);
      if (d < Math.min(bestDist, VERTEX_SNAP_THRESHOLD)) {
        bestDist = d;
        best = { x: v[0], y: v[1], type: 'vertex' };
      }
    }

    // Check edges
    for (let i = 0; i < verts.length; i++) {
      const a = verts[i];
      const b = verts[(i + 1) % verts.length];
      const cp = closestPointOnSegment(cursorX, cursorY, a[0], a[1], b[0], b[1]);
      if (cp.dist < bestDist) {
        bestDist = cp.dist;
        best = {
          x: cp.x, y: cp.y, type: 'edge',
          edgeFrom: a, edgeTo: b,
        };
      }
    }
  }

  return best;
}
